"use client";

import React, { useEffect, useMemo, useState } from "react";

import { Container, Row, Col, Spinner, Button } from "react-bootstrap";
import {
  Eye,
  ArrowLeft,
  Share2,
  MessageCircle,
  Twitter,
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react";
import Link from "next/link";

import { BlogSidebar } from "@/app/components/blog/BlogSidebar";
import { BlogPost, BlogCategory, BlogComment } from "@/app/types/blog";
import {
  fetchPopularPosts,
  fetchBlogCategorySummary,
  fetchPostComments,
} from "@/app/services/blogApi";
import { useToast } from "@/app/hooks/use-toast";
// import MakeWebsiteLiveSection from "@/app/components/blog/MakeWebsiteLiveSection";
import { getAuthorAvatar } from "@/app/utils/authorImage";
import Image from "next/image";
import BlogAd0 from "../../../../public/aiimg/blgbnr0.png"
interface BlogDetailContentProps {
  post: BlogPost;
}

type TocItem = {
  id: string;
  text: string;
  level: number;
};

type CommentNode = BlogComment & { replies: CommentNode[] };

const decodeHtmlEntities = (value: string): string =>
  value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&ldquo;/g, "“")
    .replace(/&rdquo;/g, "”")
    .replace(/&lsquo;/g, "‘")
    .replace(/&rsquo;/g, "’");

const buildCommentTree = (comments: BlogComment[]): CommentNode[] => {
  const nodes = new Map<string, CommentNode>();
  const roots: CommentNode[] = [];

  comments.forEach((comment, index) => {
    const node: CommentNode = {
      ...comment,
      id: comment.id || String(index + 1),
      replies: [],
    };
    nodes.set(node.id, node);
  });

  nodes.forEach((node) => {
    const parentId =
      typeof node.parentId === "number"
        ? String(node.parentId)
        : node.parentId?.toString();
    if (parentId && nodes.has(parentId)) {
      nodes.get(parentId)!.replies.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (list: CommentNode[]) =>
    list.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

  const sortTree = (list: CommentNode[]) => {
    sortNodes(list);
    list.forEach((node) => sortTree(node.replies));
  };

  sortTree(roots);
  return roots;
};

const BlogDetailContent: React.FC<BlogDetailContentProps> = ({ post }) => {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [popularPosts, setPopularPosts] = useState<BlogPost[]>([]);
  const [totalPosts, setTotalPosts] = useState<number>(0);
  const [loadingSidebar, setLoadingSidebar] = useState(true);
  const [comments, setComments] = useState<CommentNode[]>([]);
  const [commentsVisible, setCommentsVisible] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [preTocContent, setPreTocContent] = useState<string>("");
  const [postTocContent, setPostTocContent] = useState<string>(
    post.content || ""
  );
  const { toast } = useToast();

  const slugify = useMemo(
    () => (value: string) =>
      value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, ""),
    []
  );

  const shareUrl = useMemo(() => {
    if (typeof window !== "undefined" && window.location) {
      return window.location.href;
    }
    return `https://www.temok.com/blog/${post.slug}`;
  }, [post.slug]);

  const shareItems = useMemo(() => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(post.title);
    return [
      {
        name: "Twitter",
        icon: Twitter,
        href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      },
      {
        name: "Facebook",
        icon: Facebook,
        href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      },
      {
        name: "LinkedIn",
        icon: Linkedin,
        href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      },
      {
        name: "Instagram",
        icon: Instagram,
        href: `https://www.instagram.com/?url=${encodedUrl}`,
      },
    ];
  }, [shareUrl, post.title]);

  useEffect(() => {
    if (typeof window === "undefined" || !post.content) {
      setPreTocContent("");
      setPostTocContent(post.content || "");
      setTocItems([]);
      return;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(post.content, "text/html");
      const headings = Array.from(
        doc.querySelectorAll("h2, h3, h4, h5")
      ) as HTMLElement[];

      const items: TocItem[] = [];
      const slugTracker: Record<string, number> = {};

      headings.forEach((heading) => {
        const text = heading.textContent?.trim();
        if (!text) return;

        let baseSlug = slugify(text);
        if (!baseSlug) return;

        if (slugTracker[baseSlug] !== undefined) {
          slugTracker[baseSlug] += 1;
          baseSlug = `${baseSlug}-${slugTracker[baseSlug]}`;
        } else {
          slugTracker[baseSlug] = 0;
        }

        heading.id = baseSlug;
        const level = Number(heading.tagName.substring(1));
        items.push({ id: baseSlug, text, level });
      });

      setTocItems(items);

      const preContainer = doc.createElement("div");
      const postContainer = doc.createElement("div");
      let encounteredHeading = false;

      const isBlockTag = (tagName: string) =>
        [
          "P",
          "DIV",
          "H1",
          "H2",
          "H3",
          "H4",
          "H5",
          "H6",
          "UL",
          "OL",
          "LI",
          "TABLE",
          "THEAD",
          "TBODY",
          "TFOOT",
          "TR",
          "TD",
          "TH",
          "FIGURE",
          "BLOCKQUOTE",
          "PRE",
          "SECTION",
          "ARTICLE",
        ].includes(tagName);

      const appendInlineNode = (
        target: HTMLElement,
        node: Node,
        forceNewParagraph = false
      ) => {
        let paragraph = target.lastElementChild as HTMLElement | null;
        if (!paragraph || paragraph.tagName !== "P" || forceNewParagraph) {
          paragraph = doc.createElement("p");
          target.appendChild(paragraph);
        }
        paragraph.appendChild(node);
      };

      doc.body.childNodes.forEach((node) => {
        const isHeading =
          node.nodeType === Node.ELEMENT_NODE &&
          /^H[2-5]$/i.test((node as Element).tagName);

        const target =
          encounteredHeading || isHeading ? postContainer : preContainer;

        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node.cloneNode(true) as HTMLElement;
          if (isBlockTag(element.tagName)) {
            target.appendChild(element);
          } else {
            appendInlineNode(target, element);
          }
        } else if (node.nodeType === Node.TEXT_NODE) {
          const textContent = node.textContent || "";
          if (!textContent.trim()) return;

          const segments = textContent.split(/\r?\n\r?\n+/);
          segments.forEach((segment, index) => {
            const trimmed = segment.trim();
            if (!trimmed) return;

            const textNode = doc.createTextNode(trimmed.replace(/\r?\n/g, " "));
            appendInlineNode(target, textNode, index > 0);
          });
        }

        if (isHeading && !encounteredHeading) {
          encounteredHeading = true;
        }
      });

      if (encounteredHeading) {
        setPreTocContent(preContainer.innerHTML);
        setPostTocContent(postContainer.innerHTML);
      } else {
        setPreTocContent("");
        setPostTocContent(preContainer.innerHTML || postContainer.innerHTML);
      }
    } catch (error) {
      console.warn("Failed to build table of contents:", error);
      setPreTocContent("");
      setPostTocContent(post.content || "");
      setTocItems([]);
    }
  }, [post.content, slugify]);

  useEffect(() => {
    const loadSidebarData = async () => {
      try {
        const [categorySummary, popularResult] = await Promise.all([
          fetchBlogCategorySummary(),
          fetchPopularPosts(7),
        ]);

        if (categorySummary.categories.length > 0) {
          setCategories(categorySummary.categories);
        } else {
          setCategories([]);
        }

        setTotalPosts(Number(categorySummary.total) || 0);

        if (popularResult.length > 0) {
          setPopularPosts(popularResult);
        } else {
          setPopularPosts([]);
        }
      } catch {
        toast({
          title: "Error",
          description: "Failed to load sidebar data.",
          variant: "destructive",
        });
      } finally {
        setLoadingSidebar(false);
      }
    };
    loadSidebarData();
  }, [toast]);

  const authorName =
    typeof post.author === "object"
      ? post.author.name
      : post.author || "Unknown Author";

  // Use local author images from /public/blog/authorImages/
  const fallbackAvatar =
    typeof post.author === "object"
      ? post.author.avatar
      : post.avatar || "https://i.pravatar.cc/100?img=7";

  const authorAvatar = getAuthorAvatar(authorName, fallbackAvatar);

  const handleLoadComments = async () => {
    if (commentsVisible) {
      setCommentsVisible(false);
      return;
    }

    setCommentsVisible(true);
    if (comments.length > 0 || commentsLoading) {
      return;
    }

    setCommentsLoading(true);
    try {
      const response = await fetchPostComments(post.slug);
      const enriched = response.map((comment) => ({
        ...comment,
        content: decodeHtmlEntities(comment.content),
      }));
      setComments(buildCommentTree(enriched));
    } catch (error) {
      console.error("Failed to load comments", error);
      toast({
        title: "Error",
        description: "Unable to fetch comments right now.",
        variant: "destructive",
      });
    } finally {
      setCommentsLoading(false);
    }
  };

  const formatParagraphs = (text: string): string => {
    if (!text) return "";

    // // If HTML tags already exist, assume formatting is done — don’t add extra <p> or <br>
    // if (/<(p|a|div|h[1-6]|ul|ol|li|img|blockquote|code|pre|strong|em|table|figure)[^>]*>/i.test(text)) {
    //   return text
    //     .trim()
    //     .replaceAll("<a ", " <a ")
    //     .replace(/(<\/a>)([a-zA-Z0-9])/g, "$1 $2")
    //     .replace(/(<a\b[^>]*\bhref=["'])https?:\/\/(?:www\.)?blog\.temok\.com/gi, "$1https://blog.temok.com")
    //     .replace(/(<a\b[^>]*\bhref=["'])https?:\/\/blog\.temok\.com/gi, "$1https://www.temok.com/blog");
    if (
      /<(p|a|div|h[1-6]|ul|ol|li|img|blockquote|code|pre|strong|em|table|figure)[^>]*>/i.test(
        text
      )
    ) {
      return (
        text
          .trim()
          .replaceAll("<a ", " <a ")
          .replace(/(<\/a>)([a-zA-Z0-9])/g, "$1 $2")

          // 1) For ALL URLs (a, img, etc.): www.blog.temok.com -> blog.temok.com
          .replace(
            /https?:\/\/www\.blog\.temok\.com\/([^\s"'<>)]*)/gi,
            "https://blog.temok.com/$1"
          )

          // 2) For <a href="..."> only: blog.temok.com -> https://www.temok.com/blog/...
          .replace(
            /(<a\b[^>]*\bhref=["'])https?:\/\/(?:www\.)?blog\.temok\.com(\/[^\s"'<>)]*)?/gi,
            "$1https://www.temok.com/blog$2"
          )
      );
    }

    // Otherwise, format plain text
    const blocks = text
      .split(/\n\s*\n+/) // Split on multiple line breaks
      .map((block) => block.trim())
      .filter(Boolean);

    return blocks
      .map((block) => `<p>${block.replace(/\n/g, " ")}</p>`)
      .join("");
  };

  const renderComments = (nodes: CommentNode[], depth = 0) => (
    <div className={depth ? "ms-4 mt-3" : "mt-4"}>
      {nodes.map((node) => (
        <div
          key={node.id}
          className="p-3 bg-white border rounded-4 shadow-sm mb-3"
          style={{ borderLeft: depth ? "3px solid #f36b21" : undefined }}
        >
          <div className="d-flex align-items-center gap-3 mb-2">
            <Image
              src={
                node.authorAvatar ||
                `https://i.pravatar.cc/100?u=${encodeURIComponent(node.author)}`
              }
              alt={node.author}
              width={40}
              height={40}
              className="rounded-circle"
            />
            <div>
              <h6 className="mb-0 fw-semibold">{node.author}</h6>
              <small className="text-muted">
                {new Date(node.createdAt).toLocaleString()}
              </small>
            </div>
          </div>
          <div
            className="text-secondary"
            dangerouslySetInnerHTML={{ __html: node.content }}
          />
          {node.replies.length > 0 && renderComments(node.replies, depth + 1)}
        </div>
      ))}
    </div>
  );

  const handleTocClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    id: string
  ) => {
    event.preventDefault();
    if (typeof window === "undefined") return;
    const target = document.getElementById(id);
    if (!target) {
      window.location.hash = id;
      return;
    }

    const headerOffset = 100;
    const elementPosition = target.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - headerOffset;

    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  };

  return (
    <div className="bg-light min-vh-100">
      <Container className="py-5">
        <Link href="/blog" className="btn text-decoration-none mb-4 p-0 goBack">
          <ArrowLeft size={16} className="me-2" />
          Back to Blog
        </Link>

        <Row className="g-5">
          <Col md={12} lg={8}>
            <article>
              <h1 className="display-4 fw-bold mb-3">{post.title}</h1>

              <div className="d-flex align-items-center gap-3 mb-4"></div>

              <div className="d-flex flex-wrap gap-4 mb-4 pb-4 border-bottom text-muted">
                <div className="d-flex align-items-center gap-2">
                  {" "}
                  <Image
                    src={authorAvatar}
                    alt={authorName}
                    width={64}
                    height={64}
                    className="rounded-circle border"
                  />
                  <div>
                    <h6 className="mb-0 fw-semibold">{authorName}</h6>
                    <small className="text-muted">
                      {new Date(post.publishedAt).toLocaleDateString()} •{" "}
                      {post.readTime || "5 min read"}
                    </small>
                  </div>
                </div>

                {/* <div className="d-flex align-items-center gap-2">
                  <Eye size={18} />
                  <span>{post.views?.toLocaleString()} views</span>
                </div> */}

                <div className="share-inline ms-auto d-flex align-items-center gap-2">
                  <Share2 size={18} className="text-muted" />
                  <span className="small text-muted fw-semibold">Share:</span>
                  {shareItems.map(({ name, icon: Icon, href }) => (
                    <a
                      key={name}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Share on ${name}`}
                      className="share-icon-btn"
                    >
                      <Icon size={18} />
                    </a>
                  ))}
                </div>
              </div>

              {post.image && (
                <Image
                  src={post.image}
                  alt={post.title}
                  width={1200}
                  height={500}
                  className="img-fluid rounded mb-5 w-100 shadow-sm"
                  style={{ objectFit: "cover" }}
                />
              )}

              <div className="fs-5 lh-lg">
                <div className="blog-detail-content">
                  {preTocContent && (
                    // <div
                    //   className="content-inner prose prose-lg max-w-none"
                    //   dangerouslySetInnerHTML={{
                    //     __html: preTocContent,
                    //   }}
                    // />

                    <div
                      className="content-inner prose prose-lg max-w-none"
                      dangerouslySetInnerHTML={{
                        __html:
                          tocItems.length > 0
                            ? formatParagraphs(preTocContent)
                            : formatParagraphs(post.content),
                      }}
                    />
                  )}

                  {tocItems.length > 0 && (
                    <div className="toc-container bg-white border rounded-4 shadow-sm p-4 mb-5">
                      <h5
                        className="fw-semibold mb-3 toc-heading"
                        style={{
                          borderBottom: "2px solid #f36b21",
                          display: "inline-block",
                          paddingBottom: "4px",
                        }}
                      >
                        Table of Contents
                      </h5>
                      <ul
                        className="mb-0 ps-3 toc-list"
                        style={{ listStyleType: "disc" }}
                      >
                        {tocItems.map((item) => (
                          <li
                            key={item.id}
                            className="mb-2 toc-item"
                            style={{
                              marginLeft: `${Math.max(0, item.level - 2) * 16}px`,
                            }}
                          >
                            <a
                              href={`#${item.id}`}
                              className="toc-link"
                              onClick={(event) =>
                                handleTocClick(event, item.id)
                              }
                            >
                              {item.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="content-segment">
                    <div
                      className="content-inner prose prose-lg max-w-none"
                      dangerouslySetInnerHTML={{
                        __html:
                          tocItems.length > 0
                            ? formatParagraphs(postTocContent)
                            : formatParagraphs(post.content),
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <Button
                  variant="outline-primary"
                  onClick={handleLoadComments}
                  disabled={commentsLoading}
                  className="d-inline-flex align-items-center gap-2 btnRose"
                >
                  <MessageCircle size={16} />
                  {commentsVisible ? "Hide Comments" : "Show Comments"}
                </Button>

                {commentsVisible && (
                  <div className="mt-4">
                    {commentsLoading ? (
                      <Spinner animation="border" variant="primary" />
                    ) : comments.length > 0 ? (
                      renderComments(comments)
                    ) : (
                      <p className="text-muted">No comments yet.</p>
                    )}
                  </div>
                )}
              </div>
            </article>
          </Col>

          <Col md={12} lg={4}>
            <div className="sticky-top" style={{ top: "1rem" }}>
              {loadingSidebar ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="text-muted mt-2">Loading sidebar...</p>
                </div>
              ) : (
                <BlogSidebar
                  categories={categories}
                  popularPosts={popularPosts}
                  selectedCategory={""}
                  totalCount={totalPosts}
                  onCategorySelect={() => {}}
                />
              )}
            </div>
          </Col>
        </Row>
        <div className="my-5">
         <Link href="/linux-shared-hosting-usa"> <Image src={BlogAd0} width={1400} alt="..." className="img-fluid rounded-5"/></Link>
        </div>
        {/* <MakeWebsiteLiveSection /> */}
      </Container>
      <style>{`
        .blog-detail-content {
          color: #000000;
          font-family: "Poppins", "Segoe UI", Arial, sans-serif;
          font-size: 1.05rem;
          line-height: 1.75;
          overflow-wrap: break-word;
          word-break: break-word;
        }
        .blog-detail-content p {
          margin-bottom: 1.35rem;
          line-height: 1.7;
        }
        .blog-detail-content h2,
        .blog-detail-content h3,
        .blog-detail-content h4,
        .blog-detail-content h5,
        .blog-detail-content h6,
        .content-inner h2,
        .content-inner h3,
        .content-inner h4,
        .content-inner h5,
        .content-inner h6 {
          margin-top: 2rem;
          margin-bottom: 1rem;
          line-height: 1.3;
        }
        
        .blog-detail-content a,
        .content-inner a {
          color: #e66f2e;
          text-decoration: underline;
          text-decoration-thickness: 1.5px;
          text-decoration-color: #e66f2e;
          transition:
            color 0.2s ease,
            text-decoration-color 0.2s ease,
            background 0.2s ease;
        }
        .content-inner p strong:has(+ a) {
          display: block;
        }
        .blog-detail-content a:hover,
        .content-inner a:hover {
          color: #c8571f;
          text-decoration-color: #c8571f;
        }
        .content-inner {
          width: 100%;
          overflow: hidden;
        }
        .content-inner img,
        .content-inner picture,
        .content-inner video,
        .content-inner iframe,
        .content-inner embed {
          display: block;
          max-width: 100%;
          height: auto;
          margin: 1.5rem auto;
        }
        .content-inner img.aligncenter,
        .content-inner figure.aligncenter {
          display: block;
          margin-left: auto;
          margin-right: auto;
        }
        .content-inner img.alignleft,
        .content-inner figure.alignleft {
          float: left;
          margin-right: 1rem;
        }
        .content-inner img.alignright,
        .content-inner figure.alignright {
          float: right;
          margin-left: 1rem;
        }
        .content-inner [class*="wp-image-"],
        .content-inner [class*="size-"] {
          max-width: 100%;
          height: auto;
        }
        .content-inner figure {
          width: 100%;
          margin: 1.5rem auto;
        }
        .content-inner figure > img {
          width: 100%;
        }
        .content-inner iframe,
        .content-inner video,
        .content-inner embed {
          aspect-ratio: 16 / 9;
          width: 100%;
          border: none;
        }
        .content-inner blockquote {
          border-left: 4px solid #e66f2d;
          padding: 1rem 1.5rem;
          margin: 2rem 0;
          background: rgba(230, 111, 45, 0.05);
          font-style: italic;
          color: #333;
        }
        .content-inner pre,
        .content-inner code {
          background: #f7f8fa;
          border-radius: 12px;
          padding: 0.85rem 1rem;
          font-family:
            "Fira Code", "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          overflow-x: auto;
          display: block;
        }
        .content-inner ul,
        .content-inner ol {
          padding-left: 1.4rem;
          margin-bottom: 1.5rem;
        }
        .content-inner table {
          width: 100%;
          border-collapse: collapse;
          margin: 2rem 0;
          overflow-x: auto;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }
        .content-inner table th,
        .content-inner table td {
          padding: 0.85rem 1rem;
          border: 1px solid #e5e7eb;
          text-align: left;
          background: #ffffff;
        }
        .content-inner table th {
          background: rgba(230, 111, 45, 0.08);
          font-weight: 600;
        }
        .content-inner img,
        .content-inner video {
          border-radius: 18px;
          box-shadow: 0 12px 35px rgba(11, 23, 57, 0.08);
        }
        .toc-link {
          color: #000000;
          text-decoration: underline;
          text-decoration-color: #e66f2e;
          text-decoration-thickness: 2px;
          transition:
            color 0.2s ease,
            text-decoration-color 0.2s ease;
        }
        .toc-link:hover {
          color: #e66f2e;
          text-decoration-color: #e66f2e;
        }
        .toc-container {
          margin-top: 1.5rem;
        }
        .toc-heading {
          font-size: 1rem;
        }
        .toc-item {
          line-height: 1.4;
        }
        @media (max-width: 992px) {
          .blog-detail-content {
            font-size: 1rem;
          }
          .toc-container {
            padding: 1.25rem;
          }
        }
        @media (max-width: 768px) {
          .blog-detail-content {
            font-size: 0.97rem;
            line-height: 1.65;
          }
          .content-inner img.alignleft,
          .content-inner img.alignright,
          .content-inner figure.alignleft,
          .content-inner figure.alignright {
            float: none;
            margin: 1rem auto !important;
          }
          .toc-container {
            margin-top: 1rem;
          }
          .toc-heading {
            font-size: 0.95rem;
          }
        }
        .share-inline {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
        }
        .share-icon-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          border: 1px solid rgba(230, 111, 46, 0.4);
          color: #e66f2e;
          transition:
            background-color 0.2s ease,
            color 0.2s ease,
            border-color 0.2s ease;
        }
        .share-icon-btn:hover {
          background-color: #e66f2e;
          color: #ffffff;
          border-color: #e66f2e;
        }
      `}</style>
    </div>
  );
};

export default BlogDetailContent;
